import React, { useState } from 'react';
import { membershipFeesAPI } from '../../services/api';
import type { MembershipFeeSettings } from '../../types/membershipFee';

interface Props {
  settings: MembershipFeeSettings;
  onSettingsUpdated: (settings: MembershipFeeSettings) => void;
}

const FeeSettingsPanel: React.FC<Props> = ({ settings, onSettingsUpdated }) => {
  const [amount, setAmount] = useState(settings.defaultMonthlyAmount.toString());
  const [reminderEnabled, setReminderEnabled] = useState(settings.reminderEnabled);
  const [reminderDay, setReminderDay] = useState(settings.reminderDayOfMonth || 10);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 0) {
      setError('Érvényes összeget adj meg');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await membershipFeesAPI.updateSettings({
        defaultMonthlyAmount: parsed,
        reminderEnabled,
        reminderDayOfMonth: reminderDay,
      });
      onSettingsUpdated(updated);
    } catch {
      setError('Hiba történt a mentés során');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Tagdíj beállítások</h3>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">{error}</div>
      )}
      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="defaultAmount" className="block text-sm font-medium text-gray-700 mb-1">
            Alapértelmezett havi tagdíj (Ft)
          </label>
          <input
            id="defaultAmount"
            type="number"
            min="0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500 w-48"
          />
          <p className="mt-1 text-xs text-gray-500">
            A „Tagdíjak generálása” ezzel számol (akinek egyedi díja van, az azt kapja). 0 Ft-os tagdíj nem készül:
            ha itt 0 marad, generáláskor kell megadni az összeget.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 p-3">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={reminderEnabled}
              onChange={e => setReminderEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">Automatikus emlékeztető a tartozóknak</span>
              <span className="block text-xs text-gray-500">
                A szülők emailt és értesítést kapnak a befizetetlen hónapokról. Egy tételről havonta legfeljebb egyszer szólunk.
              </span>
            </span>
          </label>
          {reminderEnabled && (
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
              Minden hónap
              <select
                value={reminderDay}
                onChange={e => setReminderDay(parseInt(e.target.value, 10))}
                className="rounded-md border border-gray-300 px-2 py-1 focus:border-primary-500 focus:outline-none"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}.</option>
                ))}
              </select>
              napján, reggel 9-kor
            </label>
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="self-start px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Mentés...' : 'Mentés'}
        </button>
      </div>
      {settings.updatedByUserName && (
        <p className="text-xs text-gray-400 mt-2">
          Utoljára módosította: {settings.updatedByUserName} ({new Date(settings.updatedAt).toLocaleDateString('hu-HU')})
        </p>
      )}
    </div>
  );
};

export default FeeSettingsPanel;
